'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { BodyLarge, Column } from '../../styles';
import { TextButton } from '../buttons';
import { Comment } from '../../types';
import {
    ButtonsGrid,
    CommentContainer,
    CommentRow,
    CommentsButton,
    DeletedComment,
    UserReference
} from './styles';
import { useToggle, useUser } from '../../hooks';
import { CommentInput } from './comment-input/CommentInput';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { assertUnreachable, flattenAllChildrenFromComment, sortByCreatedAt } from '../../utils';
import { CommentEditMenu } from './comment-edit-menu/CommentEditMenu';
import { DELETED_COMMENT } from '../../../constants';
import { CommentInfo } from './comment-info/CommentInfo';
import { Box } from '@mui/material';
import { useTranslations } from 'next-intl';

export enum ThreadLevel {
    Root,
    First,
    Second
}

type Props = {
    comment: Comment;
    threadLevel: ThreadLevel;
    isLastEntry?: boolean;
    openParentInput?: (replyToUser: Comment) => void;
    onReply: (comment: string, parentCommentId?: string) => void;
    onDelete: (comment: Comment) => void;
    onUpdate: (comment: Comment) => void;
};

export const CommentEntry = ({
    comment,
    threadLevel,
    openParentInput,
    isLastEntry = false,
    onReply,
    onDelete,
    onUpdate
}: Props) => {
    const t = useTranslations('details');
    const { user } = useUser({ withProfilePicture: true });
    const [inEditMode, toggleEditMode] = useToggle();
    const [inputState, setInputState] = useState<{ open: boolean; replyTo?: Comment }>({
        open: false
    });

    const [answersVisible, setAnswersVisible] = useState(0);

    // Update visible comments
    useEffect(() => {
        setAnswersVisible(comment.children.length === 1 ? 1 : 0);
    }, [comment.children.length]);

    const isSecondLevelComment = threadLevel === ThreadLevel.Second;
    const canToggleRepliesOnRoot = threadLevel === ThreadLevel.Root && comment.children.length > 1;
    const canToggleRepliesOnFirstLevel =
        threadLevel === ThreadLevel.First && comment.children.length > 1;

    const { sortedReplies, canToggleMoreAnswers } = useMemo(() => {
        switch (threadLevel) {
            case ThreadLevel.Root:
                const sorted = sortByCreatedAt(comment.children).slice(0, answersVisible);
                return {
                    sortedReplies: sorted,
                    canToggleMoreAnswers: comment.children.length > sorted.length
                };
            case ThreadLevel.First:
                const flattenedChildren = flattenAllChildrenFromComment(comment);
                const sortedChildren = sortByCreatedAt(flattenedChildren).slice(0, answersVisible);
                return {
                    sortedReplies: sortedChildren,
                    canToggleMoreAnswers:
                        sortedChildren.length > 0 &&
                        flattenedChildren.length > sortedChildren.length
                };
            case ThreadLevel.Second:
                return { sortedReplies: [], canToggleMoreAnswers: false };
            default:
                assertUnreachable(threadLevel);
        }
    }, [comment, threadLevel, answersVisible]);

    if (!user) return null;

    const showAnswers = answersVisible > 0;
    const isDeletedComment = comment.content === DELETED_COMMENT;
    const isOwnComment = comment.author.id === user.id;

    const onSubmitReply = (text: string) => {
        const parentComment = inputState.replyTo ?? comment;
        onReply(text, parentComment.id.toString());
    };

    const onReplyToComment = () =>
        isSecondLevelComment
            ? openParentInput?.(comment)
            : setInputState({ open: true, replyTo: comment });

    const onToggleReplies = () => {
        if (showAnswers) {
            setAnswersVisible(0);
            // also close the input
            setInputState({ open: false, replyTo: undefined });
        } else setAnswersVisible(10);
    };

    const renderToggleAnswersButtons = () => {
        if (!canToggleRepliesOnFirstLevel && !canToggleMoreAnswers) return null;
        return (
            <ButtonsGrid>
                <Box />
                {canToggleRepliesOnFirstLevel ? (
                    <CommentsButton
                        icon={showAnswers ? <ExpandLess /> : <ExpandMore />}
                        label={
                            showAnswers
                                ? t('hide_replies', { count: '' })
                                : t('show_replies', { count: comment.children.length })
                        }
                        onClick={onToggleReplies}
                    />
                ) : (
                    <Box />
                )}
                {canToggleMoreAnswers && (
                    <CommentsButton
                        label={t('more_comments')}
                        onClick={() =>
                            setAnswersVisible((visibleAnswers) =>
                                visibleAnswers < 10 ? 10 : visibleAnswers + 10
                            )
                        }
                    />
                )}
            </ButtonsGrid>
        );
    };

    return (
        <CommentContainer level={threadLevel} isLastEntry={isLastEntry}>
            {inEditMode ? (
                <CommentInput
                    author={user}
                    onSubmitComment={(content) => {
                        onUpdate({ ...comment, content });
                        toggleEditMode();
                    }}
                    initialValue={comment.content}
                />
            ) : (
                <CommentRow id={comment.id.toString()}>
                    <CommentInfo
                        author={comment.author}
                        createdAt={comment.createdAt}
                        updatedAt={comment.updatedAt}
                    />

                    <Column>
                        {comment.parentRelation && isSecondLevelComment && (
                            <UserReference href={`#${comment.parentRelation.id}`}>
                                {t('reply_to_comment', {
                                    user: comment.parentRelation.author.name
                                })}
                            </UserReference>
                        )}
                        {isDeletedComment ? (
                            <DeletedComment>{t('deleted_comment')}</DeletedComment>
                        ) : (
                            <BodyLarge>{comment.content}</BodyLarge>
                        )}
                    </Column>
                    {canToggleRepliesOnRoot && (
                        <TextButton
                            icon={showAnswers ? <ExpandLess /> : <ExpandMore />}
                            label={showAnswers ? t('hide_replies') : t('show_replies')}
                            onClick={onToggleReplies}
                        />
                    )}
                    {isDeletedComment ? null : isOwnComment ? (
                        <CommentEditMenu
                            onEdit={toggleEditMode}
                            onDelete={() => onDelete(comment)}
                        />
                    ) : (
                        <CommentsButton label={t('reply')} onClick={onReplyToComment} />
                    )}
                </CommentRow>
            )}
            {sortedReplies.map((reply, i) => (
                <CommentEntry
                    key={reply.id}
                    comment={reply}
                    threadLevel={(threadLevel + 1) as ThreadLevel}
                    openParentInput={(replyTo: Comment) => {
                        setInputState({ open: true, replyTo });
                    }}
                    onReply={(text) => onReply(text, reply.id.toString())}
                    isLastEntry={i === sortedReplies.length - 1}
                    onDelete={() => onDelete(reply)}
                    onUpdate={onUpdate}
                />
            ))}
            {renderToggleAnswersButtons()}
            {inputState.open && (
                <CommentInput
                    author={user}
                    replyTo={inputState.replyTo?.author.name}
                    onSubmitComment={onSubmitReply}
                />
            )}
        </CommentContainer>
    );
};
